import React, { useEffect, useState } from "react";
import TextArea from "antd/es/input/TextArea";
import analyzeImg from "@assets/img/analyze.svg";
import "./Detect.scss";
import pusher from "@utils/Pusher";
import { Progress } from "antd";
import Helpers from "@utils/Helpers";
import "animate.css";
import businessLogicApi from "services/BusinessLogicApi";
import { IDetect } from "@utils/Interfaces";
import { useTour } from "@reactour/tour";
import { DetectSteps } from "@components/GuidedTour/steps/DetectSteps";

const Detect: React.FC<IDetect> = ({
  campaign,
  visible,
  campaignCurrentStep,
  setCampaignCurrentStep,
  industriesKeyword,
  setIndustriesKeyword,
  positionsKeyword,
  setPositionsKeyword,
  aboutCompany,
  setAboutCompany,
  jobDescription,
  setJobDescription,
}) => {
  const { setSteps, setCurrentStep } = useTour();
  const [loading, setLoading] = useState(false);
  const [percentLoading, setPercentLoading] = useState(0);
  const businessPayload = Helpers.businessPayload;
  const notification = Helpers.notification;

  // HANDLE GUIDED TOUR
  useEffect(() => {
    if (visible) {
      if (industriesKeyword.length == 0 && !loading) {
        const newStep = DetectSteps.filter(
          (item, index) => index !== 3 && index !== 4 && index !== 5
        );
        setSteps?.(newStep);
      }

      if (loading) {
        const newStep = DetectSteps.filter(
          (item, index) => index !== 4 && index !== 5
        );
        setSteps?.(newStep);
      }

      if (industriesKeyword.length > 0) {
        const newStep = DetectSteps.filter((item, index) => index !== 3);
        setTimeout(() => {
          setSteps?.(newStep);
        }, 500);
      }
    }
  }, [industriesKeyword, loading, visible]);

  // HANDLE CHANGE DATA FROM TEXT AREA
  const textAreaChange = (e: any, key: string) => {
    key === "aboutCompany"
      ? setAboutCompany(e.target.value)
      : setJobDescription(e.target.value);
    setIndustriesKeyword([]);
    setPositionsKeyword([]);
    setPercentLoading(0);
  };

  // HANDLE CLEAR TEXT AREA
  const clearTextArea = (key: string) => {
    key === "aboutCompany" ? setAboutCompany("") : setJobDescription("");
    setIndustriesKeyword([]);
    setPositionsKeyword([]);
    setPercentLoading(0);
  };

  // HANDLE RUN PROGRESS BAR
  useEffect(() => {
    if (loading) {
      const intervalId = setInterval(() => {
        setPercentLoading((percent) => {
          if (percent >= 100) {
            clearInterval(intervalId);
            return 100;
          }
          return percent + 1;
        });
      }, 230);

      return () => clearInterval(intervalId);
    }
  }, [loading]);

  // HANDLE ANALYZE
  const handleAnalyze = async () => {
    setIndustriesKeyword?.([]);
    setPositionsKeyword?.([]);
    setLoading(true);
    try {
      const response = await businessLogicApi.Script_0(
        businessPayload.script(0, {
          company_description: aboutCompany,
          job_description: jobDescription,
          campaign_input_id: campaign.campaign_input_id,
        }),
        campaign.campaign_id
      );
      if (response.status === 200) {
        handleTriggerPusher(response.data);
      }
    } catch (error) {
      setLoading(false);
      setPercentLoading(0);
      notification.error("Can not analyze. Please try again");
    }
  };

  // LISTEN EVENT FROM PUSHER
  const handleTriggerPusher = (data: any) => {
    pusher(data, (message) => {
      setPositionsKeyword?.(JSON.parse(message.related_positions));
      setIndustriesKeyword?.(JSON.parse(message.related_industries));
    });
  };

  // HANDLE DELETE KEYWORD
  const deleteKeyword = (value: any, key: string) => {
    if (key === "industry") {
      var index = industriesKeyword.indexOf(value);
      if (index !== -1) {
        const newData: any = [...industriesKeyword];
        newData.splice(index, 1);
        setIndustriesKeyword(newData);
      }
    } else {
      var index = positionsKeyword.indexOf(value);
      if (index !== -1) {
        const newData: any = [...positionsKeyword];
        newData.splice(index, 1);
        setPositionsKeyword(newData);
      }
    }
  };

  return (
    <div className={`Detect row mt-5 ${visible !== true ? "d-none" : ""}`}>
      <div className="col-4">
        <div className="d-flex flex-column gap-4">
          <div className="form-group-area">
            <button
              className="btn-clear"
              onClick={(): void => clearTextArea("aboutCompany")}
            >
              <i className="small text-muted fa-solid fa-xmark"></i>
            </button>
            <div className={`form-title ${aboutCompany ? "active" : ""}`}>
              About Your Company
            </div>
            <TextArea
              rows={10}
              className="w-100 form-area form-area-about"
              value={aboutCompany}
              onFocus={() => {
                aboutCompany === "" && setAboutCompany(" ");
              }}
              onBlur={() => {
                aboutCompany === " " && setAboutCompany("");
              }}
              onChange={(e: any) => textAreaChange(e, "aboutCompany")}
            />
          </div>
          <div className="form-group-area">
            <div className={`form-title ${jobDescription ? "active" : ""}`}>
              Job Description
            </div>
            <TextArea
              rows={10}
              className="w-100 form-area form-area-job"
              value={jobDescription}
              onFocus={() => {
                jobDescription === "" && setJobDescription(" ");
              }}
              onBlur={() => {
                jobDescription === " " && setJobDescription("");
              }}
              onChange={(e: any) => textAreaChange(e, "jobDescription")}
            />
            <button
              className="btn-clear"
              onClick={(): void => clearTextArea("jobDescription")}
            >
              <i className="small text-muted fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
      </div>
      <div
        className={`col-8 ${industriesKeyword.length !== 0 ? "" : "d-none"}`}
      >
        <div className="analyze-result-group h-100">
          <div className="analyze-result-domain">
            <div className="analyze-result-title mb-2">Industries</div>
            <div className="analyze-domain">
              {industriesKeyword.length > 0
                ? industriesKeyword.map((item: any, index: any) => {
                    return (
                      <div
                        className="analyze-result-item"
                        data-item={item}
                        key={index}
                      >
                        <div className="keyword-item">
                          {item}
                          <span
                            onClick={() => deleteKeyword(item, "industry")}
                            className="btn-remove-keyword text-muted small"
                          >
                            <i className="fa-solid fa-xmark text-muted"></i>
                          </span>
                        </div>
                      </div>
                    );
                  })
                : ""}
            </div>
          </div>
          <div className="analyze-result-position">
            <div className="analyze-result-title mb-2">Positions</div>
            <div className="analyze-position">
              {positionsKeyword.length > 0
                ? positionsKeyword.map((item: any, index: any) => {
                    return (
                      <div
                        className="analyze-result-item"
                        key={index}
                        data-item={item}
                      >
                        <div className="keyword-item">
                          {item}
                          <span
                            onClick={() => deleteKeyword(item, "position")}
                            className="btn-remove-keyword text-muted small"
                          >
                            <i className="fa-solid fa-xmark text-muted"></i>
                          </span>
                        </div>
                      </div>
                    );
                  })
                : ""}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`col-7 ${industriesKeyword.length === 0 ? "" : "d-none"}`}
      >
        <div className="h-100 d-flex justify-content-center align-items-center">
          <div className="analyze-review w-75 d-flex justify-content-center align-items-center flex-column gap-4">
            <img src={analyzeImg} alt="" />
            <div className="analyze-review-title text-center">
              In order to analyze company and job description, please enter the
              job description and about your company in the textarea section,
              and press the analyze button.
            </div>
            <Progress
              className={`progress-analyze ${
                loading ? "visible" : "invisible"
              }`}
              trailColor="transparent"
              strokeColor="#5ac6c6"
              percent={percentLoading}
              size="small"
              status="active"
            />
          </div>
        </div>
      </div>
      <div className="card-submit mt-3 px-2">
        <div className="form-submit d-flex">
          <button
            className="me-auto btn-heppai btn-analyze"
            onClick={handleAnalyze}
            disabled={industriesKeyword.length == 0 ? false : true}
          >
            Analyze
          </button>
          <button
            className="ms-auto btn-heppai px-4 py-2 fw-bold btn-continue"
            onClick={() => setCampaignCurrentStep?.(1)}
            disabled={industriesKeyword.length == 0 ? true : false}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Detect;
